import {
  type ChangeEvent,
  type FormEvent,
  forwardRef,
  useCallback,
  useState,
  type ForwardedRef,
} from "react";
import { RapButton } from "@dynata/rex-raptor";
import "../../../../scss/manage-users.scss";
import type { OrganizationData } from "../../../../services/management-backend/client";
import {
  multiAccountRoles,
  organizationLevelRoles,
  singleAccountRoles,
} from "../manageUsers.page";
import type { IUpdateUserRequestModel } from "@lib/types";
import type User from "models/user";
import { type Role, rolesToUpdateAccountAssignments } from "models/user";
import type Account from "models/account";

const INACTIVE = "Inactive";
const ADMIN = "Admin";
const ORGANIZATION_OWNER = "Organization Owner";

export type EditPopupProps = {
  onClose: () => void;
  user: User;
  onUpdateUserClick: (body: IUpdateUserRequestModel) => void;
  onDeleteUserClick: () => void;
  organizationAccounts: Account[];
  organizationData: OrganizationData;
};

type EditUserFormProps = {
  user: User;
  organizationAccounts: Account[];
  organizationData: OrganizationData;
  onClose: () => void;
  onUpdate: (body: IUpdateUserRequestModel) => void;
  onDelete: () => void;
};

type DeleteUserConfirmationProps = {
  username: string;
  onConfirm: () => void;
  onCancel: () => void;
};

type RoleFieldProps = {
  id: string;
  label: string;
  options: string[];
  disabled?: boolean;
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  value?: string;
  defaultValue?: string;
};

function toRole(account: Account, role: string): Role {
  return {
    accountId: account.id,
    accountNumber: account.number,
    accountName: account.name,
    role,
    isAdhoc: account.isAdhoc,
  };
}

function RoleField({
  id,
  label,
  options,
  disabled,
  onChange,
  value,
  defaultValue,
}: RoleFieldProps) {
  return (
    <div className="flex input-container">
      <label
        className="dark_mode_wrapper"
        htmlFor={id}
      >
        {label}
      </label>
      <select
        className="dark_mode_wrapper"
        id={id}
        name={id}
        onChange={onChange}
        disabled={disabled}
        {...(value === undefined ? { defaultValue } : { value })}
      >
        {options.map((option) => (
          <option
            key={option}
            value={option}
          >
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

function DeleteUserConfirmation({
  username,
  onConfirm,
  onCancel,
}: DeleteUserConfirmationProps) {
  return (
    <>
      <p>
        Are you sure you want to delete this user <strong>{username}</strong>?
      </p>
      <div className="flex btn-container">
        <div>
          <RapButton
            type="button"
            color="danger"
            onClick={onConfirm}
          >
            Confirm
          </RapButton>
        </div>
        <div className="right-btn-section">
          <RapButton
            type="button"
            styleType="secondary"
            color="alternative"
            onClick={onCancel}
          >
            Cancel
          </RapButton>
        </div>
      </div>
    </>
  );
}

function EditUserForm({
  user,
  organizationAccounts,
  organizationData,
  onClose,
  onUpdate,
  onDelete,
}: EditUserFormProps) {
  const [formValues, setFormValues] = useState<Role[]>(user.roles);
  const [formState, setFormState] = useState({
    formSubmitted: false,
    isOrganizationAdmin: user.isAdmin,
    isAccountRolesLocked: user.isAdmin && organizationAccounts.length > 1,
  });

  const isMultiAccount = organizationAccounts.length > 1;
  const organizationName =
    organizationData?.display_name || organizationData?.name;
  const hasActiveRole = formValues.some(({ role }) => role !== INACTIVE);

  const handleSelectChange = useCallback(
    (id: number, event: ChangeEvent<HTMLSelectElement>) => {
      const account = organizationAccounts.find((value) => value.id === id);
      if (!account) {
        return;
      }
      setFormValues((prevState) => {
        const role = event.target.value;
        const withoutAccount = prevState.filter(
          (value) => value.accountId !== id,
        );
        if (role === INACTIVE) {
          return withoutAccount;
        }
        if (role === ORGANIZATION_OWNER) {
          // Organization owners get set to the "Admin" role AND is_organization_admin = true.
          // Regardless if it is a single or multi-account organization.
          setFormState((prevFormState) => ({
            ...prevFormState,
            isOrganizationAdmin: true,
          }));
          return [...withoutAccount, toRole(account, ADMIN)];
        }
        if (isMultiAccount) {
          // For multi-account organizations - do not change the admin flag from the role select options.
          return [...withoutAccount, toRole(account, role)];
        }
        // For single-account organizations - make sure to update the admin flag to false for anything other than "Organization Owner".
        setFormState((prevFormState) => ({
          ...prevFormState,
          isOrganizationAdmin: false,
        }));
        return [...withoutAccount, toRole(account, role)];
      });
    },
    [organizationAccounts, isMultiAccount],
  );

  const setAllAccountsToAdmin = useCallback(() => {
    setFormValues(
      organizationAccounts.map((account) => toRole(account, ADMIN)),
    );
  }, [organizationAccounts]);

  const handleOrganizationSelectChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const role = event.target.value;
      if (role === ORGANIZATION_OWNER) {
        setFormState((prevState) => ({
          ...prevState,
          isAccountRolesLocked: true,
          isOrganizationAdmin: true,
        }));
        setAllAccountsToAdmin();
        return;
      }
      setFormState((prevState) => ({
        ...prevState,
        isAccountRolesLocked: false,
        isOrganizationAdmin: false,
      }));
    },
    [setAllAccountsToAdmin],
  );

  const onSubmitForm = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      setFormState((prevState) => ({ ...prevState, formSubmitted: true }));
      if (!hasActiveRole) {
        return;
      }
      onUpdate({
        is_organization_admin: formState.isOrganizationAdmin,
        accounts: rolesToUpdateAccountAssignments(formValues),
      });
    },
    [formValues, formState.isOrganizationAdmin, hasActiveRole, onUpdate],
  );

  return (
    <>
      <span className="label-title">Organization</span>

      {isMultiAccount && (
        <>
          <RoleField
            id="organization-select"
            label={organizationName ?? ""}
            options={organizationLevelRoles}
            defaultValue={user.isAdmin ? ORGANIZATION_OWNER : "Contributor"}
            onChange={handleOrganizationSelectChange}
          />
          <span className="label-title">Accounts</span>
          {formState.formSubmitted && !hasActiveRole && (
            <label className="error-label">
              * At least one Account Role selection is required
            </label>
          )}
        </>
      )}

      <form
        className="edit-popup-form"
        onSubmit={onSubmitForm}
      >
        {organizationAccounts.map(({ name, id }) => (
          <RoleField
            key={id}
            id={`dropdown-${id}`}
            label={isMultiAccount ? name : organizationName || name}
            options={isMultiAccount ? multiAccountRoles : singleAccountRoles}
            disabled={formState.isAccountRolesLocked}
            onChange={(event) => handleSelectChange(id, event)}
            value={
              !isMultiAccount && formState.isOrganizationAdmin
                ? ORGANIZATION_OWNER
                : formValues.find((value) => value.accountId === id)?.role ||
                  INACTIVE
            }
          />
        ))}
        <div className="flex btn-container">
          <div>
            <RapButton
              type="button"
              color="danger"
              onClick={onDelete}
            >
              Delete
            </RapButton>
          </div>
          <div className="right-btn-section">
            <RapButton
              type="button"
              styleType="secondary"
              color="alternative"
              onClick={onClose}
            >
              Close
            </RapButton>
            <RapButton
              type="submit"
              styleType="primary"
            >
              Update
            </RapButton>
          </div>
        </div>
      </form>
    </>
  );
}

const EditUserPopup = forwardRef<HTMLDialogElement, EditPopupProps>(
  (
    {
      onClose,
      user,
      onUpdateUserClick,
      onDeleteUserClick,
      organizationAccounts,
      organizationData,
    }: EditPopupProps,
    ref: ForwardedRef<HTMLDialogElement>,
  ) => {
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

    return (
      <dialog
        className="manageUser_popup_wrapper overlay"
        ref={ref}
      >
        <div className="model-popup-container">
          <div className="edit-popup-container">
            <div>
              <span className="label-title">
                {user.firstName} {user.lastName} ({user.username})
              </span>
            </div>
            {isConfirmingDelete ? (
              <DeleteUserConfirmation
                username={user.username}
                onConfirm={() => {
                  setIsConfirmingDelete(false);
                  onDeleteUserClick();
                }}
                onCancel={() => setIsConfirmingDelete(false)}
              />
            ) : (
              <EditUserForm
                user={user}
                organizationAccounts={organizationAccounts}
                organizationData={organizationData}
                onClose={onClose}
                onUpdate={onUpdateUserClick}
                onDelete={() => setIsConfirmingDelete(true)}
              />
            )}
          </div>
        </div>
      </dialog>
    );
  },
);

export default EditUserPopup;
